import React, { useCallback } from "react";
import { Form, Switch, InputNumber, Flex, Tooltip, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { IMasterDataType } from "../../interfaces/master-data";
import { ERuleDefaultSettings, ERuleDefaultPaymentTolerances, ERuleDefaultNominalCodes } from "../../constants/enum";

type NamePath = string | number | (string | number)[];

interface SupplierRuleProps {
  name: NamePath;
  accountDebits: IMasterDataType[];
  accountCredits: IMasterDataType[];
  autosMatch: IMasterDataType[];
  isDefault?: boolean;
  isEdit?: boolean;
}

const SupplierRule: React.FC<SupplierRuleProps> = ({
  name,
  accountDebits = [],
  accountCredits = [],
  autosMatch = [],
  isDefault = false,
  isEdit = true
}) => {
  const base = Array.isArray(name) ? name : [name];

  const getName = (field: string): NamePath => [...base, "rule", field];
  
  const getValue = (getFieldValue: (path: NamePath) => any, field: string): any => {
    const namePath = [...base, "rule", field];
    return getFieldValue(isDefault ? namePath : ["paymentTermsRules", ...namePath]);
  };

  const getOptionsByField = useCallback(
    (fieldName: string): IMasterDataType[] => {
      switch (fieldName) {
        case "sageAccountDebit":
          return accountDebits;
        case "sageAccountCredit":
          return accountCredits;
        case "undersOversAutoMatchCode":
          return autosMatch;
        default:
          return [];
      }
    },
    [accountDebits, accountCredits, autosMatch]
  );

  const renderSelect = (fieldName: string, disabled: boolean) => {
    const options = getOptionsByField(fieldName);
    return (
      <Select
        disabled={disabled}
        showSearch
        allowClear
        placeholder="Select"
        filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        options={options.map(({ code, label }) => ({ value: code, label }))}
      />
    );
  };

  const renderSwitchExtraInput = (field: any, fieldAll: string, getFieldValue: (name: NamePath) => any) => {
    const isFieldAllActive = getValue(getFieldValue, fieldAll);
    const isFieldActive = getValue(getFieldValue, field.name);
    const isDisabled = isFieldAllActive || !isFieldActive || !isEdit;

    return (
      <Flex style={{ width: "200px" }} gap={12}>
        <Form.Item name={getName(field.name)} valuePropName="checked">
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            disabled={(isFieldAllActive && fieldAll !== field.name) || !isEdit}
          />
        </Form.Item>

        {field.extraInput && (
          <Form.Item
            name={getName(field.extraInput)}
            rules={[
              {
                required: !isDisabled,
                message: "",
              },
              {
                validator: (_, value) => {
                  if (isDisabled) {
                    return Promise.resolve();
                  }
                  if (field?.typeExtraInput === "percentage") {
                    if (value < 1 || value > 100) {
                      return Promise.reject(new Error("Must be between 1 and 100"));
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} disabled={isDisabled} />
          </Form.Item>
        )}

        {field.selectInput && (
          <Form.Item
            name={getName(field.selectInput)}
            style={{ width: "100%" }}
            rules={[{ required: !isDisabled, message: "" }]}
          >
            {renderSelect(field.selectInput, isDisabled)}
          </Form.Item>
        )}
      </Flex>
    );
  };

  const renderBox = (rules: any[], fieldAll: string) => (
    <>
      {rules.map((field, index) => (
        <Flex
          key={field.name}
          justify="space-between"
          align="center"
          className={`item-rule ${index === 0 ? "last" : ""}`}
        >
          <Flex align="center" gap={6} className="label">
            <span>{`${field.label} ${index === 0 && !isDefault ? "- Apply Default": ""}`}</span>
            {field.tooltip && (
              <Tooltip title={field.tooltip}>
                <FontAwesomeIcon icon={faInfoCircle} size="sm" />
              </Tooltip>
            )}
          </Flex>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => renderSwitchExtraInput(field, fieldAll, getFieldValue)}
          </Form.Item>
        </Flex>
      ))}
    </>
  );

  return (
    <>
      <h3 style={{ marginTop: 36 }}>Rules</h3>
      <div className="box-rule">
        {renderBox(ERuleDefaultSettings, ERuleDefaultSettings[0].name)}
        {renderBox(ERuleDefaultPaymentTolerances, ERuleDefaultPaymentTolerances[0].name)}
        {renderBox(ERuleDefaultNominalCodes, ERuleDefaultNominalCodes[0].name)}
      </div>
    </>
  );
};

export default SupplierRule;
